import { useCallback } from 'react'

import { useGridAnalysisJob } from './useGridAnalysisJob'
import {
  TailgateGridTestJobResult,
  useTailgateGridTestJob,
} from './useTailgateGridTestJob'

import {
  BoxInput,
  GridAnalysisJob,
  GridAnalysisJobStatus,
  TailgateGridTestJobStatus,
} from '@/graphql/generated/schemas'

export const GRID_TESTING_ZONE_JOB_STATUS = {
  completed: 'completed',
  failed: 'failed',
  pending: 'pending',
} as const

type GridTestingZoneJobStatus =
  | (typeof GRID_TESTING_ZONE_JOB_STATUS)[keyof typeof GRID_TESTING_ZONE_JOB_STATUS]
  | null

interface StartGridTestingZoneJobIProps {
  /** Camera whose grid is under test (tailgate experience only); see ENG-34041. */
  cameraId?: string | null
  incidentId: string
  inBox: BoxInput
  labeledIn: number
  labeledOut: number
  outBox: BoxInput
}

interface UseGridTestingZoneJobIProps {
  isTailgateExperience: boolean
  onCompleted?: () => void
  onLegacyFailed?: (isTimeout: boolean) => void
  onTailgateFailed?: (isTimeout: boolean) => void
}

interface UseGridTestingZoneJobResultIProps {
  hasJob: boolean
  isTimeout: boolean
  legacyJob?: GridAnalysisJob
  reset: () => void
  start: (input: StartGridTestingZoneJobIProps) => Promise<void>
  status: GridTestingZoneJobStatus
  tailgateJob?: TailgateGridTestJobResult
}

const getLegacyStatus = (
  status: GridAnalysisJobStatus | null
): GridTestingZoneJobStatus => {
  switch (status) {
    case GridAnalysisJobStatus.Pending:
      return GRID_TESTING_ZONE_JOB_STATUS.pending
    case GridAnalysisJobStatus.Completed:
      return GRID_TESTING_ZONE_JOB_STATUS.completed
    case GridAnalysisJobStatus.Failed:
      return GRID_TESTING_ZONE_JOB_STATUS.failed
    default:
      return null
  }
}

const getTailgateStatus = (
  status: TailgateGridTestJobStatus | null
): GridTestingZoneJobStatus => {
  switch (status) {
    case TailgateGridTestJobStatus.Pending:
      return GRID_TESTING_ZONE_JOB_STATUS.pending
    case TailgateGridTestJobStatus.Completed:
      return GRID_TESTING_ZONE_JOB_STATUS.completed
    case TailgateGridTestJobStatus.Failed:
      return GRID_TESTING_ZONE_JOB_STATUS.failed
    default:
      return null
  }
}

/**
 * Adapts the legacy detector job and the production-evaluator job to one
 * controller contract while the feature flag must support both experiences.
 */
export const useGridTestingZoneJob = ({
  isTailgateExperience,
  onCompleted,
  onLegacyFailed,
  onTailgateFailed,
}: UseGridTestingZoneJobIProps): UseGridTestingZoneJobResultIProps => {
  const legacy = useGridAnalysisJob({
    onCompleted,
    onFailed: onLegacyFailed,
  })
  const tailgate = useTailgateGridTestJob({
    isEnabled: isTailgateExperience,
    onCompleted,
    onFailed: onTailgateFailed,
  })

  const reset = useCallback(() => {
    legacy.reset()
    tailgate.reset()
  }, [legacy.reset, tailgate.reset])

  const start = useCallback(
    async ({
      cameraId,
      incidentId,
      inBox,
      labeledIn,
      labeledOut,
      outBox,
    }: StartGridTestingZoneJobIProps) => {
      if (isTailgateExperience) {
        await tailgate.start({
          cameraId,
          incidentId,
          securedBox: outBox,
          unsecuredBox: inBox,
        })
        return
      }

      await legacy.start({
        incidentId,
        inBox,
        labeledIn,
        labeledOut,
        outBox,
      })
    },
    [isTailgateExperience, legacy.start, tailgate.start]
  )

  const status = isTailgateExperience
    ? getTailgateStatus(tailgate.status)
    : getLegacyStatus(legacy.status)
  const isTimeout = isTailgateExperience ? tailgate.isTimeout : legacy.isTimeout
  const hasJob = isTailgateExperience
    ? Boolean(tailgate.job)
    : Boolean(legacy.job)

  return {
    hasJob,
    isTimeout,
    legacyJob: legacy.job,
    reset,
    start,
    status,
    tailgateJob: tailgate.job,
  }
}
